import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import HttpRoutingService from "../../shared/services/httpRoutingService";
import { API } from "../../shared/apiRoutes";
import { getManualTax, initialData, updateTaxValue, zoneRes, getOnePosType, duplicateName, removeProductRes, taxCategoryProducts, payloadType, TaxData, TempItem } from "../../components/tax/taxModule";

interface commonType {
    loading: boolean,
    error: string
}

interface PayloadProps {
    storeId: number,
    data: initialData | undefined
}

export interface updateTaxCategoriesProp { id: number | undefined, isDeleted?: boolean, isEnabled?: boolean, storeId: number, statusUpdateOnly: boolean }
export interface checkDuplicateProps { taxId: string | null, taxName: string }
export interface removeTaxProps { isAddProduct: boolean, isAllProductsEnabled: boolean, isTaxableProducts: number[], mappingId: number[], productIds: number[], taxId: number }
export interface createTaxProps {
    id: number | null;
    storeId: number;
    name: string;
    description: string;
    isEnabledAllProducts: boolean;
    taxFor: string;
    taxType: number;
    otherZoneRate: string | null;
    isEnabled: boolean | undefined;
    zones: TempItem[];
    statusUpdateOnly: boolean;
    taxId: number;
    productIds: number[];
    isAllProductsEnabled: boolean;
    mappingId: never[];
    isTaxableProducts: number[];
    isAddProduct: boolean;
    medium: string;
}

interface IntialValueProps {
    loading: boolean,
    taxValue: getManualTax | null,
    rows: TaxData[],
    count: number,
    isReached: boolean,
    filter: string | null,
    error: string,
    updateorDeleteTax: commonType & { value: updateTaxValue | null },
    createTax: commonType & { value: updateTaxValue | null },
    updateTax: commonType & { value: updateTaxValue | null },
    getAllPosZones: commonType & { value: zoneRes | null },
    getOnePosTax: commonType & { value: getOnePosType | null },
    checkDuplicateTax: commonType & { value: duplicateName | null },
    removeTaxRes: commonType & { value: removeProductRes | null },
    getPosTaxProductRes: commonType & { value: taxCategoryProducts | null, rows: any[], count: number, isReached: boolean, filter: string | null }
}

const initialState: IntialValueProps = {
    loading: true,
    taxValue: null,
    rows: [],
    count: 0,
    isReached: false,
    filter: null,
    error: '',
    updateorDeleteTax: { value: null, loading: true, error: "" },
    getAllPosZones: { value: null, loading: true, error: "" },
    getOnePosTax: { value: null, loading: true, error: "" },
    checkDuplicateTax: { value: null, loading: false, error: "" },
    removeTaxRes: { value: null, loading: true, error: "" },
    createTax: { value: null, loading: false, error: "" },
    updateTax: { value: null, loading: false, error: "" },
    getPosTaxProductRes: { value: null, loading: true, error: "", rows: [], count: 0, isReached: false, filter: null }
}

export const getAllPosManualTax = createAsyncThunk("tax/getAllPosManualTax", ({ storeId, data }: PayloadProps) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllPosManualTax, { storeId: storeId })
    return HttpRoutingService?.getMethod<getManualTax>(url, data)
})

export const updateorDeleteTax = createAsyncThunk("tax/updateorDeleteTax", ({ storeId, data }: { storeId: number, data: updateTaxCategoriesProp }) => {
    const url = HttpRoutingService?.replaceUrl(API?.updateorDeleteTax, { storeId: storeId, taxId: data?.id })
    return HttpRoutingService?.putMethod<updateTaxValue>(url, data)
})
export const createTax = createAsyncThunk("tax/createTax", ({ storeId, data }: { storeId: number, data: createTaxProps }) => {
    const url = HttpRoutingService?.replaceUrl(API?.createTaxCategories, { storeId: storeId })
    return HttpRoutingService?.postMethod<updateTaxValue>(url, data)
})
export const updateTax = createAsyncThunk("tax/updateTax", ({ storeId, data }: { storeId: number, data: createTaxProps }) => {
    const url = HttpRoutingService?.replaceUrl(API?.updateorDeleteTax, { storeId: storeId, taxId: data?.taxId })
    return HttpRoutingService?.putMethod<updateTaxValue>(url, data)
})

export const getAllPosZones = createAsyncThunk("tax/getAllPosZones", ({ storeId }: { storeId: number }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllPosZones, { storeId: storeId })
    return HttpRoutingService?.getMethod<zoneRes>(url)
})

export const getOnePosTax = createAsyncThunk("tax/getOnePosTax", ({ storeId, id }: { storeId: number, id: number }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getOnePosTax, { storeId: storeId, id: id })
    return HttpRoutingService?.getMethod<getOnePosType>(url)
})

export const checkDuplicateTaxName = createAsyncThunk("tax/checkDuplicateTaxName", ({ storeId, data }: { storeId: number, data: checkDuplicateProps }) => {
    const url = HttpRoutingService?.replaceUrl(API?.checkDuplicateTax, { storeId: storeId })
    return HttpRoutingService?.getMethod<duplicateName>(url, data, "v1/")
})

export const removeTaxProducts = createAsyncThunk("tax/removeTaxProducts", ({ storeId, data }: { storeId: number, data: removeTaxProps }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getPosTaxProducts, { storeId: storeId })
    return HttpRoutingService?.putMethod<removeProductRes>(url, data)
})

export const getPosTaxProducts = createAsyncThunk("tax/getPosTaxProducts", ({ storeId, data }: { storeId: number, data: payloadType | undefined }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getPosTaxProducts, { storeId: storeId })
    return HttpRoutingService?.getMethod<taxCategoryProducts>(url, data)
})
export const taxSlice = createSlice({
    name: "tax",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getAllPosManualTax?.pending, (state) => {
                state.loading = true
            })
            .addCase(getAllPosManualTax?.fulfilled, (state, action) => {
                state.loading = false
                state.taxValue = action?.payload

                const offset = action.meta.arg.data?.offset ?? 0
                const limit = action.meta.arg.data?.limit ?? 10
                const incoming = action.payload?.taxCategory?.rows ?? []

                if (offset > 0) {
                    state.rows = [...state.rows, ...incoming]
                } else {
                    state.rows = incoming
                    state.count = action.payload?.taxCategory?.count ?? 0
                }

                state.isReached = incoming.length < limit
                state.filter = JSON.stringify({
                    searchText: action.meta.arg.data?.globalSearch,
                    filterValue: action.meta.arg.data?.filterData,
                })
            })
            .addCase(getAllPosManualTax?.rejected, (state, action) => {
                state.loading = false
                state.error = action?.error?.message as string
            })
            .addCase(updateorDeleteTax?.pending, (state) => {
                state.updateorDeleteTax.loading = true
            })
            .addCase(updateorDeleteTax?.fulfilled, (state, action) => {
                state.updateorDeleteTax.loading = false
                state.updateorDeleteTax.value = action?.payload
            })
            .addCase(updateorDeleteTax?.rejected, (state, action) => {
                state.updateorDeleteTax.loading = false
                state.updateorDeleteTax.error = action?.error?.message as string
            })

            .addCase(createTax?.pending, (state) => {
                state.createTax.loading = true
            })
            .addCase(createTax?.fulfilled, (state, action) => {
                state.createTax.loading = false
                state.createTax.value = action?.payload
            })
            .addCase(createTax?.rejected, (state, action) => {
                state.createTax.loading = false
                state.createTax.error = action?.error?.message as string
            })

            .addCase(updateTax?.pending, (state) => {
                state.updateTax.loading = true
            })
            .addCase(updateTax?.fulfilled, (state, action) => {
                state.updateTax.loading = false
                state.updateTax.value = action?.payload
            })
            .addCase(updateTax?.rejected, (state, action) => {
                state.updateTax.loading = false
                state.updateTax.error = action?.error?.message as string
            })

            .addCase(getAllPosZones?.pending, (state) => {
                state.getAllPosZones.loading = true
            })
            .addCase(getAllPosZones?.fulfilled, (state, action) => {
                state.getAllPosZones.loading = false
                state.getAllPosZones.value = action?.payload
            })
            .addCase(getAllPosZones?.rejected, (state, action) => {
                state.getAllPosZones.loading = false
                state.getAllPosZones.error = action?.error?.message as string
            })
            .addCase(getOnePosTax?.pending, (state) => {
                state.getOnePosTax.loading = true
            })
            .addCase(getOnePosTax?.fulfilled, (state, action) => {
                state.getOnePosTax.loading = false
                state.getOnePosTax.value = action?.payload
            })
            .addCase(getOnePosTax?.rejected, (state, action) => {
                state.getOnePosTax.loading = false
                state.getOnePosTax.error = action?.error?.message as string
            })
            .addCase(checkDuplicateTaxName?.pending, (state) => {
                state.checkDuplicateTax.loading = true
            })
            .addCase(checkDuplicateTaxName?.fulfilled, (state, action) => {
                state.checkDuplicateTax.loading = false
                state.checkDuplicateTax.value = action?.payload
            })
            .addCase(checkDuplicateTaxName?.rejected, (state, action) => {
                state.checkDuplicateTax.loading = false
                state.checkDuplicateTax.error = action?.error?.message as string
            })
            .addCase(removeTaxProducts?.pending, (state) => {
                state.removeTaxRes.loading = true
            })
            .addCase(removeTaxProducts?.fulfilled, (state, action) => {
                state.removeTaxRes.loading = false
                state.removeTaxRes.value = action?.payload
            })
            .addCase(removeTaxProducts?.rejected, (state, action) => {
                state.removeTaxRes.loading = false
                state.removeTaxRes.error = action?.error?.message as string
            })
            .addCase(getPosTaxProducts?.pending, (state) => {
                state.getPosTaxProductRes.loading = true
            })
            .addCase(getPosTaxProducts?.fulfilled, (state, action) => {
                state.getPosTaxProductRes.loading = false
                state.getPosTaxProductRes.value = action?.payload
                console.log(action?.payload, "----------------------");


                const offset = action.meta.arg.data?.offset ?? 0
                const limit = action.meta.arg.data?.limit ?? 10
                const incoming = action.payload?.taxCategoryProducts?.rows ?? []

                if (offset > 0) {
                    state.getPosTaxProductRes.rows = [...state.getPosTaxProductRes.rows, ...incoming]
                } else {
                    state.getPosTaxProductRes.rows = incoming
                    state.getPosTaxProductRes.count = action.payload?.taxCategoryProducts?.count ?? 0
                }

                state.getPosTaxProductRes.isReached = incoming.length < limit
                state.getPosTaxProductRes.filter = JSON.stringify({
                    searchText: action.meta.arg.data?.searchText,
                    isEnabledProduct: action.meta.arg.data?.isEnabledProduct,
                    sortOrder: action.meta.arg.data?.sortOrder,
                    isProductMapped: action.meta.arg.data?.isProductMapped,
                    taxId: action.meta.arg.data?.taxId
                })
            })
            .addCase(getPosTaxProducts?.rejected, (state, action) => {
                state.getPosTaxProductRes.loading = false
                state.getPosTaxProductRes.error = action?.error?.message as string
            })
    }
})

export default taxSlice?.reducer